import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { CheckIcon, ClockIcon, GlassesIcon, ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon } from 'lucide-react';
import { DocumentStreamVisualization } from './DocumentStreamVisualization';
import { BrandedEmailInterface } from './BrandedEmailInterface';
// Email Reminder Animation Component
const EmailReminderAnimation = () => {
  const [stage, setStage] = useState('checklist'); // 'checklist', 'email', 'reminder'
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [showCheckAnimation, setShowCheckAnimation] = useState(false);
  // Documents to request - reduced to 3 items
  const documents = [{
    id: 1,
    name: 'ID Verification',
    checked: true
  }, {
    id: 2,
    name: 'Proof of Address',
    checked: true
  }, {
    id: 3,
    name: 'Employment Contract',
    checked: true
  }];
  // Cycle through the animation stages
  useEffect(() => {
    const stageTimeline = async () => {
      // Start with checklist
      setStage('checklist');
      // After 4 seconds, show the email
      await new Promise(resolve => setTimeout(resolve, 4000));
      setStage('email');
      // After 4 more seconds, show the reminder
      await new Promise(resolve => setTimeout(resolve, 4000));
      setStage('reminder');
      // After 4 more seconds, go back to checklist
      await new Promise(resolve => setTimeout(resolve, 4000));
      setStage('checklist');
    };
    // Start the timeline
    stageTimeline();
    // Set up an interval to repeat the timeline
    const interval = setInterval(stageTimeline, 12000);
    return () => clearInterval(interval);
  }, []);
  // Handle send button click
  const handleSendClick = () => {
    setIsButtonClicked(true);
    setShowCheckAnimation(true);
    setTimeout(() => {
      setShowCheckAnimation(false);
      setIsButtonClicked(false);
      setStage('email');
    }, 1000);
  };
  // Handle indicator click
  const handleIndicatorClick = selectedStage => {
    setStage(selectedStage);
  };
  return <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#f8faff] to-[#f0f5ff]" data-id="element-617">
      {/* Step indicators at the top of the animation */}
      <div className="flex items-center space-x-3 mb-4" data-id="element-618">
        {['checklist', 'email', 'reminder'].map((step, index) => <motion.button key={`step-${index}`} className="relative h-3 w-3 rounded-full bg-white/50 backdrop-blur-sm cursor-pointer" style={{
        boxShadow: '0 0 10px rgba(46,107,255,0.2)'
      }} onClick={() => handleIndicatorClick(step)} whileHover={{
        scale: 1.2
      }} whileTap={{
        scale: 0.9
      }} data-id="element-619">
            <motion.div className="absolute inset-0 rounded-full bg-[#2E6BFF]" initial={{
          scale: 0
        }} animate={{
          scale: stage === step ? 1 : 0,
          boxShadow: stage === step ? '0 0 15px rgba(46,107,255,0.5)' : 'none'
        }} transition={{
          duration: 0.3
        }} data-id="element-620" />
          </motion.button>)}
      </div>
      <AnimatePresence mode="wait" data-id="element-621">
        {/* Rest of the component content */}
      </AnimatePresence>
    </div>;
};
// AI Verification Engine Demo Component
const AIVerificationDemo = () => {
  const [currentStep, setCurrentStep] = useState(1); // Start at step 1 (extracting) instead of 0 (scanning)
  const [extractedFields, setExtractedFields] = useState([]);
  const [fieldStatuses, setFieldStatuses] = useState({});
  const [progressValue, setProgressValue] = useState(0); // Added for continuous progress
  const [isPaused, setIsPaused] = useState(false); // New state to track pause
  const [showNotification, setShowNotification] = useState(false); // New state to control notification visibility
  // Fields to extract and verify - removed "Total Amount" field
  const fields = [{
    id: 'invoice',
    label: 'Invoice Number',
    value: 'INV-2024-001',
    status: 'pending'
  }, {
    id: 'date',
    label: 'Issue Date',
    value: '2024-03-15',
    status: 'pending'
  }, {
    id: 'signature',
    label: 'Signature',
    value: '',
    status: 'pending',
    hasIssue: true
  }];
  // Animation steps
  const steps = [{
    id: 'scanning',
    title: 'Scanning Document',
    description: 'AI analyzing document structure',
    icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-622">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12c0 1.66-4 3-9 3s-9-1.34-9-3v-12c0-1.66 4-3 9-3s9 1.34 9 3v12" data-id="element-623" />
        </svg>,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-300'
  }, {
    id: 'extracting',
    title: 'Extracting Fields',
    description: 'Identifying key information',
    icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-624">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" data-id="element-625" />
        </svg>,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-300'
  }, {
    id: 'verifying',
    title: 'Verifying Data',
    description: 'Validating extracted information',
    icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-626">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" data-id="element-627" />
        </svg>,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-100',
    borderColor: 'border-emerald-300'
  }, {
    id: 'issue',
    title: 'Issue Detected',
    description: 'Missing required signature',
    icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-628">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-629" />
        </svg>,
    color: 'text-red-500',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-300'
  }];
  // Progress bar animation
  useEffect(() => {
    // Don't run the animation if paused
    if (isPaused) return;
    // Duration for each step and total animation
    const stepDuration = 2500; // 2.5 seconds per step
    const totalDuration = stepDuration; // Duration for the current step
    // Calculate starting progress based on current step
    let startProgress = 0;
    let targetProgress = 0;
    // Set progress targets based on current step
    if (currentStep === 1) {
      // Extracting
      startProgress = 0;
      targetProgress = 33;
    } else if (currentStep === 2) {
      // Verifying
      startProgress = 33;
      targetProgress = 66;
    } else if (currentStep === 3) {
      // Issue Detected
      startProgress = 66;
      targetProgress = 100;
    }
    // Set starting progress immediately
    setProgressValue(startProgress);
    // Update progress every 50ms
    const interval = 50;
    const totalSteps = Math.ceil(totalDuration / interval);
    const progressPerStep = (targetProgress - startProgress) / totalSteps;
    const progressInterval = setInterval(() => {
      setProgressValue(prev => {
        // If we've reached or exceeded the target, maintain the target
        if (prev >= targetProgress) {
          return targetProgress;
        }
        // Otherwise increment by the calculated amount
        return prev + progressPerStep;
      });
    }, interval);
    return () => clearInterval(progressInterval);
  }, [currentStep, isPaused]);
  // Auto progress through steps and animate field extraction - FIXED ANIMATION ISSUES
  useEffect(() => {
    let timeoutIds = [];
    const runAnimation = async () => {
      // Only clear fields when resetting to step 1
      if (currentStep === 1) {
        setExtractedFields([]);
        setFieldStatuses({});
        setShowNotification(false);
      }
      // Step 1: Extracting Fields
      if (currentStep === 1) {
        // Extract fields with consistent delay
        for (let i = 0; i < fields.length; i++) {
          const timeoutId = setTimeout(() => {
            setExtractedFields(prev => [...prev, fields[i].id]);
          }, 400 * (i + 1));
          timeoutIds.push(timeoutId);
        }
        // Move to next step after all fields are extracted
        const nextStepTimeoutId = setTimeout(() => {
          setCurrentStep(2); // Move to Verifying Data
        }, 2500);
        timeoutIds.push(nextStepTimeoutId);
      }
      // Step 2: Verifying Data
      else if (currentStep === 2) {
        // Make sure all fields are marked as extracted to prevent transparency
        if (extractedFields.length < fields.length) {
          setExtractedFields(fields.map(field => field.id));
        }
        // Verify first field (invoice number)
        const invoiceTimeoutId = setTimeout(() => {
          setFieldStatuses(prev => ({
            ...prev,
            [fields[0].id]: 'verified'
          }));
        }, 400);
        timeoutIds.push(invoiceTimeoutId);
        // Verify second field (issue date)
        const dateTimeoutId = setTimeout(() => {
          setFieldStatuses(prev => ({
            ...prev,
            [fields[1].id]: 'verified'
          }));
          // Important: Move to step 3 after the same 400ms delay
          // This ensures consistent timing between verifications and step change
          const moveToIssueStepId = setTimeout(() => {
            setCurrentStep(3); // Move to Issue Detected
          }, 400);
          timeoutIds.push(moveToIssueStepId);
        }, 800);
        timeoutIds.push(dateTimeoutId);
      }
      // Step 3: Issue Detected - Mark signature field as failed immediately
      else if (currentStep === 3) {
        // Make sure all fields are marked as extracted to prevent transparency
        if (extractedFields.length < fields.length) {
          setExtractedFields(fields.map(field => field.id));
        }
        // Mark signature field as failed immediately with step change
        const signatureTimeoutId = setTimeout(() => {
          setFieldStatuses(prev => ({
            ...prev,
            [fields[2].id]: 'issue'
          }));
          // Show notification right after marking the field
          setShowNotification(true);
        }, 100); // Small delay for visual sequence
        timeoutIds.push(signatureTimeoutId);
        // Pause for 10 seconds after showing notification
        const pauseTimeoutId = setTimeout(() => {
          setShowNotification(false);
          setIsPaused(false);
          setCurrentStep(1); // Reset to first step
          setProgressValue(0); // Reset progress
          // We'll clear fields when step 1 starts again
        }, 10000);
        timeoutIds.push(pauseTimeoutId);
        // Set pause state
        setIsPaused(true);
      }
    };
    // Start the animation
    runAnimation();
    // Clean up timeouts
    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [currentStep]);
  const currentStepData = steps[currentStep];
  return <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#f8faff] to-[#f0f5ff] relative" data-id="element-630">
      {/* Background overlay with lower z-index */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#f8faff]/40 to-[#e7f2ff]/10 backdrop-blur-[1px]" data-id="element-631"></div>
      <motion.div className="w-[90%] max-w-md bg-white rounded-xl shadow-[0_10px_40px_rgba(46,107,255,0.15)] overflow-hidden relative" initial={{
      opacity: 0,
      y: 50
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} data-id="element-632">
        {/* Current Step */}
        <div className="p-3 flex flex-col items-center justify-center" data-id="element-633">
          <motion.div key={currentStepData.id} className={`flex items-center p-2 rounded-lg border ${currentStepData.bgColor} ${currentStepData.borderColor} mb-3 h-70px w-full max-w-md`} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-634">
            <div className={`mr-2 ${currentStepData.color}`} data-id="element-635">
              <motion.div animate={{
              scale: [1, 1.2, 1]
            }} transition={{
              duration: 1,
              repeat: Infinity
            }} data-id="element-636">
                {currentStepData.icon}
              </motion.div>
            </div>
            <div className="flex-1" data-id="element-637">
              <div className="text-sm font-medium" data-id="element-638">{currentStepData.title}</div>
              <div className="text-xs text-[#5A6376]" data-id="element-639">
                {currentStepData.description}
              </div>
            </div>
          </motion.div>
          {/* Centered container for fields */}
          <div className="space-y-2 w-full" data-id="element-640">
            {fields.map(field => {
            const isExtracted = extractedFields.includes(field.id);
            const status = fieldStatuses[field.id] || 'pending';
            let statusDisplay = 'Extracting...';
            let statusColor = 'text-blue-500';
            let bgColor = 'bg-blue-50';
            if (status === 'verified') {
              statusDisplay = 'Verified';
              statusColor = 'text-emerald-500';
              bgColor = 'bg-emerald-50';
            } else if (status === 'issue') {
              statusDisplay = field.hasIssue ? 'Failed' : 'Error';
              statusColor = 'text-red-500';
              bgColor = 'bg-red-50';
            }
            // Calculate if this field should show the notification
            const showFieldNotification = field.id === 'signature' && status === 'issue' && showNotification;
            return <motion.div key={field.id} className={`flex flex-col text-sm p-3 border border-[#E7EAF0] rounded-md ${bgColor} transition-colors duration-300 relative`}
            // Pre-allocate space for notification to prevent layout shifts
            style={{
              minHeight: showFieldNotification ? '140px' : '76px'
            }} initial={{
              opacity: isExtracted ? 1 : 0.5
            }} animate={{
              opacity: isExtracted ? 1 : 0.5,
              height: 'auto'
            }} transition={{
              height: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              }
            }} layout data-id="element-641">
                  <div className="flex items-center justify-between w-full h-[40px]" data-id="element-642">
                    <div className="flex-1" data-id="element-643">
                      <div className="font-medium" data-id="element-644">{field.label}</div>
                      {isExtracted && <div className="text-xs text-[#5A6376]" data-id="element-645">
                          {field.value}
                        </div>}
                    </div>
                    {/* Center-aligned status text with flex */}
                    <div className={`text-xs font-medium ${statusColor} flex items-center h-full`} data-id="element-646">
                      {statusDisplay}
                    </div>
                  </div>
                  {/* Auto-notification with improved animation */}
                  <AnimatePresence data-id="element-647">
                    {showFieldNotification && <motion.div className="mt-3 w-full overflow-hidden" initial={{
                  opacity: 0,
                  height: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  height: 'auto',
                  y: 0
                }} exit={{
                  opacity: 0,
                  height: 0,
                  y: -10
                }} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                  opacity: {
                    duration: 0.3
                  },
                  height: {
                    duration: 0.4
                  }
                }} layout data-id="element-648">
                        <div className="flex items-end" data-id="element-649">
                          <div className="flex flex-col max-w-[95%]" data-id="element-650">
                            <div className="bg-[#F0F5FF] text-xs text-[#2E6BFF] px-3 py-1.5 rounded-t-lg rounded-r-lg shadow-sm" data-id="element-651">
                              <span className="font-medium" data-id="element-652">
                                Auto-notification
                              </span>
                            </div>
                            <div className="mt-1 bg-white text-xs text-[#E53E3E] p-3 rounded-b-lg rounded-r-lg border border-[#FED7D7] shadow-sm flex items-start space-x-2" data-id="element-653">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" data-id="element-654">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-655" />
                              </svg>
                              <span data-id="element-656">
                                Hi Maria—please sign & re-upload your POA.
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </div>
      </motion.div>
    </div>;
};
// Document Sync Animation Component
const DocumentCloudSync = () => {
  const [documents, setDocuments] = useState([]);
  const [successIndicators, setSuccessIndicators] = useState({});
  // Document types with consistent branding colors
  const documentTypes = [{
    id: 'contract',
    name: 'Contract',
    color: '#2E6BFF'
  }, {
    id: 'invoice',
    name: 'Invoice',
    color: '#36D1DC'
  }, {
    id: 'id',
    name: 'ID Card',
    color: '#0BB489'
  }, {
    id: 'certificate',
    name: 'Certificate',
    color: '#F59E0B'
  }];
  // Single destination - image placeholder with updated positioning
  const imageDestination = {
    id: 'image',
    name: 'Document Storage',
    position: {
      x: 60,
      y: 40
    }
  };
  // User source position
  const userSourcePosition = {
    x: 40,
    y: 40
  };
  // Generate new documents periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (documents.length < 6) {
        const docType = documentTypes[Math.floor(Math.random() * documentTypes.length)];
        // Create a new document going to the image placeholder
        const newDocument = {
          id: Date.now(),
          type: docType,
          progress: 0,
          opacity: 1,
          scale: 0.8,
          position: {
            x: userSourcePosition.x,
            y: userSourcePosition.y
          }
        };
        setDocuments(prev => [...prev, newDocument]);
      }
    }, 1200);
    return () => clearInterval(interval);
  }, [documents.length]);
  // Animate documents to the destination
  useEffect(() => {
    if (documents.length === 0) return;
    const animationInterval = setInterval(() => {
      setDocuments(prev => prev.map(doc => {
        // Update progress
        const newProgress = doc.progress + 2;
        // Calculate new position based on progress
        const newX = userSourcePosition.x + (imageDestination.position.x - userSourcePosition.x) * (newProgress / 100);
        const newY = userSourcePosition.y + (imageDestination.position.y - userSourcePosition.y) * (newProgress / 100);
        // If document reached its destination
        if (newProgress >= 100) {
          // Show success indicator
          setSuccessIndicators(prev => ({
            ...prev,
            image: Date.now()
          }));
          // Mark for removal
          return {
            ...doc,
            progress: 100,
            position: {
              x: imageDestination.position.x,
              y: imageDestination.position.y
            },
            opacity: 0,
            scale: 0.5,
            reachedDestination: true
          };
        }
        return {
          ...doc,
          progress: newProgress,
          position: {
            x: newX,
            y: newY
          },
          // Grow slightly as it travels, then shrink as it reaches destination
          scale: newProgress < 50 ? 0.8 + newProgress * 0.004 : 1 - (newProgress - 50) * 0.01
        };
      }));
    }, 50);
    return () => clearInterval(animationInterval);
  }, [documents]);
  // Remove documents that have reached their destinations
  useEffect(() => {
    const cleanup = setInterval(() => {
      setDocuments(prev => prev.filter(doc => !doc.reachedDestination || doc.opacity > 0));
    }, 500);
    return () => clearInterval(cleanup);
  }, []);
  // Clear success indicators after a delay
  useEffect(() => {
    if (!successIndicators.image) return;
    const cleanup = setInterval(() => {
      setSuccessIndicators(prev => {
        if (prev.image && Date.now() - prev.image > 1000) {
          return {};
        }
        return prev;
      });
    }, 500);
    return () => clearInterval(cleanup);
  }, [successIndicators]);
  return <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#f8faff] to-[#f0f5ff] relative overflow-hidden" data-id="element-657">
      <div className="absolute inset-0 bg-gradient-to-r from-[#f8faff]/60 to-[#e7f2ff]/30 backdrop-blur-[1px]" data-id="element-658"></div>
      {/* Connection path with animated gradient - updated path coordinates */}
      <svg className="absolute inset-0 w-full h-full" style={{
      zIndex: 5
    }} data-id="element-659">
        <defs data-id="element-660">
          {/* Base gradient for the main path */}
          <linearGradient id="dataStreamGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-661">
            <stop offset="0%" stopColor="#2E6BFF" stopOpacity="0.2" data-id="element-662" />
            <stop offset="50%" stopColor="#36D1DC" stopOpacity="0.3" data-id="element-663" />
            <stop offset="100%" stopColor="#0BB489" stopOpacity="0.2" data-id="element-664" />
          </linearGradient>
          {/* Animated gradient overlay */}
          <linearGradient id="animatedGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-665">
            <stop offset="0%" stopColor="#2E6BFF" stopOpacity="0.7" data-id="element-666">
              <animate attributeName="offset" values="-0.5;1.5;-0.5" dur="3s" repeatCount="indefinite" data-id="element-667" />
            </stop>
            <stop offset="20%" stopColor="#36D1DC" stopOpacity="0.8" data-id="element-668">
              <animate attributeName="offset" values="-0.3;1.7;-0.3" dur="3s" repeatCount="indefinite" data-id="element-669" />
            </stop>
            <stop offset="40%" stopColor="#0BB489" stopOpacity="0.7" data-id="element-670">
              <animate attributeName="offset" values="-0.1;1.9;-0.1" dur="3s" repeatCount="indefinite" data-id="element-671" />
            </stop>
          </linearGradient>
          {/* Subtle glow effect filter */}
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-672">
            <feGaussianBlur stdDeviation="2" result="blur" data-id="element-673" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-674" />
          </filter>
        </defs>
        {/* Main path with gradient - updated coordinates */}
        <path d={`M ${userSourcePosition.x}% ${userSourcePosition.y}% C ${userSourcePosition.x + 8}% ${userSourcePosition.y - 5}%, ${imageDestination.position.x - 8}% ${imageDestination.position.y + 5}%, ${imageDestination.position.x}% ${imageDestination.position.y}%`} stroke="url(#dataStreamGradient)" strokeWidth="4" fill="none" strokeLinecap="round" style={{
        filter: 'blur(1px)'
      }} opacity="0.6" data-id="element-675" />
        {/* Animated overlay path - updated coordinates */}
        <motion.path d={`M ${userSourcePosition.x}% ${userSourcePosition.y}% C ${userSourcePosition.x + 8}% ${userSourcePosition.y - 5}%, ${imageDestination.position.x - 8}% ${imageDestination.position.y + 5}%, ${imageDestination.position.x}% ${imageDestination.position.y}%`} stroke="url(#animatedGradient)" strokeWidth="1.5" fill="none" strokeLinecap="round" filter="url(#glow)" animate={{
        pathLength: [0, 1]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'linear',
        repeatType: 'loop'
      }} data-id="element-676" />
        {/* Animated particles along the path - updated path */}
        {[...Array(4)].map((_, i) => <motion.circle key={`particle-${i}`} r="2" fill={i % 2 === 0 ? '#2E6BFF' : '#36D1DC'} filter="url(#glow)" animate={{
        offsetDistance: ['0%', '100%'],
        opacity: [0.7, 0]
      }} transition={{
        duration: 2 + i * 0.5,
        repeat: Infinity,
        delay: i * 0.6,
        ease: 'easeInOut'
      }} style={{
        offsetPath: `path('M ${userSourcePosition.x}% ${userSourcePosition.y}% C ${userSourcePosition.x + 8}% ${userSourcePosition.y - 5}%, ${imageDestination.position.x - 8}% ${imageDestination.position.y + 5}%, ${imageDestination.position.x}% ${imageDestination.position.y}%')`
      }} data-id="element-677" />)}
      </svg>
      {/* User source on the left - updated position */}
      <motion.div className="absolute transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-4 border border-[#E7EAF0]/80 z-10" style={{
      left: `${userSourcePosition.x}%`,
      top: `${userSourcePosition.y}%`,
      transform: 'translate(-50%, -50%)'
    }} animate={{
      scale: 0.9,
      boxShadow: ['0 4px 12px rgba(46,107,255,0.08)', '0 6px 16px rgba(46,107,255,0.12)', '0 4px 12px rgba(46,107,255,0.08)']
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-678">
        <div className="flex flex-col items-center" data-id="element-679">
          <div className="w-10 h-10 rounded-full bg-[#F0F5FF] flex items-center justify-center mb-1.5" data-id="element-680">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#2E6BFF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-681">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" data-id="element-682"></path>
              <circle cx="12" cy="7" r="4" data-id="element-683"></circle>
            </svg>
          </div>
          <span className="text-xs font-medium text-[#0B1220]" data-id="element-684">User</span>
        </div>
      </motion.div>
      {/* Image placeholder on the right - updated position */}
      <motion.div className="absolute transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 border border-[#E7EAF0]/80 z-10 w-48 h-56" style={{
      left: `${imageDestination.position.x}%`,
      top: `${imageDestination.position.y}%`,
      transform: 'translate(-50%, -50%)'
    }} animate={{
      scale: successIndicators.image ? [1, 1.05, 1] : [0.98, 1.02, 0.98],
      boxShadow: successIndicators.image ? ['0 4px 12px rgba(11,180,137,0.2)', '0 6px 16px rgba(11,180,137,0.3)', '0 4px 12px rgba(11,180,137,0.2)'] : ['0 4px 12px rgba(46,107,255,0.08)', '0 6px 16px rgba(46,107,255,0.12)', '0 4px 12px rgba(46,107,255,0.08)']
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-685">
        <div className="flex flex-col items-center h-full" data-id="element-686">
          <div className="w-full h-32 bg-[#F0F5FF] rounded-lg mb-4 flex items-center justify-center" data-id="element-687">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-[#2E6BFF]/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-688">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" data-id="element-689"></rect>
              <circle cx="8.5" cy="8.5" r="1.5" data-id="element-690"></circle>
              <polyline points="21 15 16 10 5 21" data-id="element-691"></polyline>
            </svg>
          </div>
          <div className="w-full h-2 bg-[#F0F5FF] rounded-full mb-2" data-id="element-692"></div>
          <div className="w-3/4 h-2 bg-[#F0F5FF] rounded-full mb-2" data-id="element-693"></div>
          <div className="w-1/2 h-2 bg-[#F0F5FF] rounded-full" data-id="element-694"></div>
          {/* Success indicator */}
          {successIndicators.image && <motion.div className="absolute -top-2 -right-2 bg-[#0BB489] text-white rounded-full w-6 h-6 flex items-center justify-center" initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} exit={{
          scale: 0
        }} data-id="element-695">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-696">
                <polyline points="20 6 9 17 4 12" data-id="element-697"></polyline>
              </svg>
            </motion.div>}
        </div>
      </motion.div>
      {/* Animated documents - updated to use new positions */}
      {documents.map(doc => <motion.div key={doc.id} className="absolute z-20" style={{
      left: `${doc.position.x}%`,
      top: `${doc.position.y}%`,
      transform: 'translate(-50%, -50%)'
    }} animate={{
      scale: doc.scale,
      opacity: doc.progress >= 100 ? 0 : 1
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} data-id="element-698">
          <div className="w-8 h-10 rounded-md flex items-center justify-center relative overflow-hidden" style={{
        backgroundColor: 'white',
        border: `1.5px solid ${doc.type.color}`
      }} data-id="element-699">
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{
          backgroundColor: doc.type.color
        }} data-id="element-700"></div>
            <div className="mt-1 flex flex-col items-center" data-id="element-701">
              <div className="w-5 h-0.5 bg-gray-200 mb-1 rounded-full" data-id="element-702"></div>
              <div className="w-3 h-0.5 bg-gray-200 rounded-full" data-id="element-703"></div>
            </div>
            {/* Subtle pulse effect when document is created */}
            {doc.progress < 20 && <motion.div className="absolute inset-0 rounded-md" style={{
          backgroundColor: doc.type.color
        }} animate={{
          opacity: [0.3, 0],
          scale: [1, 1.2]
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-704" />}
          </div>
        </motion.div>)}
    </div>;
};
// Image Carousel Component
const ImageCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  // Carousel images - updated with the provided imgur links and removed the third image
  const carouselImages = ['https://i.imgur.com/ZS7zLlf.png', 'https://i.imgur.com/1alw8FE.png'];
  // Placeholder captions for each image - updated second caption
  const imageCaptions = ['Custom-branded document request email', 'Automatic reminders sent until completion'];
  // Auto advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % carouselImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  // Manual navigation
  const goToSlide = index => {
    setCurrentSlide(index);
  };
  return <div className="w-full h-full flex flex-col relative bg-gradient-to-br from-[#f8faff] to-[#f0f5ff] rounded-[24px] overflow-hidden" data-id="element-705">
      {/* Navigation dots */}
      <div className="absolute top-4 left-0 right-0 z-10 flex justify-center space-x-2" data-id="element-706">
        {carouselImages.map((_, index) => <motion.button key={index} className="w-2.5 h-2.5 rounded-full bg-white/50 backdrop-blur-sm" onClick={() => goToSlide(index)} animate={{
        scale: currentSlide === index ? 1.2 : 1,
        backgroundColor: currentSlide === index ? 'rgba(46, 107, 255, 0.8)' : 'rgba(255, 255, 255, 0.5)'
      }} transition={{
        duration: 0.3
      }} data-id="element-707" />)}
      </div>
      {/* Image container */}
      <div className="relative w-full h-full overflow-hidden" data-id="element-708">
        <AnimatePresence initial={false} data-id="element-709">
          <motion.img key={currentSlide} src={carouselImages[currentSlide]} alt={`Document management example ${currentSlide + 1}`} className="absolute inset-0 w-full h-full object-cover" initial={{
          opacity: 0,
          x: 100
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -100
        }} transition={{
          duration: 0.5
        }} data-id="element-710" />
        </AnimatePresence>
        {/* Darker gradient overlay with caption text */}
        <div className="absolute inset-x-0 bottom-0 h-[30%] bg-gradient-to-t from-[#0B1220]/80 to-transparent z-10" data-id="element-711"></div>
        {/* Caption text */}
        <div className="absolute bottom-6 left-0 right-0 text-center z-20 px-4" data-id="element-712">
          <motion.p key={`caption-${currentSlide}`} className="text-white font-medium text-lg drop-shadow-md" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-713">
            {imageCaptions[currentSlide]}
          </motion.p>
        </div>
      </div>
      {/* Navigation arrows */}
      <button className="absolute left-4 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center text-white z-10" onClick={() => goToSlide((currentSlide - 1 + carouselImages.length) % carouselImages.length)} data-id="element-714">
        <ChevronLeftIcon className="h-5 w-5" data-id="element-715" />
      </button>
      <button className="absolute right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center text-white z-10" onClick={() => goToSlide((currentSlide + 1) % carouselImages.length)} data-id="element-716">
        <ChevronRightIcon className="h-5 w-5" data-id="element-717" />
      </button>
    </div>;
};
// Selling Points Component
export const SellingPoints = () => {
  const containerRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const sections = [{
    title: 'Stop chasing documents manually',
    description: 'Automatically send requests and reminders to clients, no email back and forth',
    icon: <ClockIcon className="h-10 w-10 text-[#2E6BFF]" data-id="element-718" />,
    image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80'
  }, {
    title: 'Stop reviewing documents manually',
    description: 'AI-Assisted Review & Data Extraction',
    icon: <GlassesIcon className="h-10 w-10 text-[#2E6BFF]" data-id="element-719" />,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80'
  }, {
    title: 'Stop storing documents manually',
    description: 'Sync with your existing systems',
    icon: <CheckIcon className="h-10 w-10 text-[#2E6BFF]" data-id="element-720" />,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5m7VgFcrz6r6GRGSLkhZcF/Instagram_Post_Design_%281%29.png'
  }];
  return <div ref={containerRef} className="relative bg-gradient-to-b from-[#E1EBFF] to-[#F0F5FF] pt-16" data-id="element-721">
      {sections.map((section, index) => {
      // Calculate the opacity for each section based on scroll progress
      // Adjusted to have sections fade in earlier
      const sectionProgress = useTransform(scrollYProgress, [index / sections.length, (index + 0.95) / sections.length], [1, 0]);
      const nextSectionProgress = useTransform(scrollYProgress, [(index - 0.7) / sections.length, index / sections.length], [0, 1]);
      // Only the first section is fully visible at start
      const initialOpacity = index === 0 ? 1 : 0;
      return <motion.section key={index} className="min-h-[50vh] flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 sticky top-0" style={{
        opacity: nextSectionProgress,
        zIndex: sections.length - index
      }} initial={{
        opacity: initialOpacity
      }} data-id="element-722">
            <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center" data-id="element-723">
              <div className="order-1 md:order-1" data-id="element-724">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-200px 0px -200px 0px',
              amount: 0.2
            }} transition={{
              duration: 0.5,
              delay: 0.05
            }} className="bg-white rounded-[24px] shadow-lg p-8" data-id="element-725">
                  <div className="mb-6" data-id="element-726">{section.icon}</div>
                  <h2 className="text-3xl font-bold text-[#0B1220] mb-4" data-id="element-727">
                    {section.title}
                  </h2>
                  <p className="text-xl text-[#5A6376] mb-6" data-id="element-728">
                    {section.description}
                  </p>
                </motion.div>
              </div>
              <motion.div className="order-2 md:order-2 relative h-[550px] rounded-[24px] overflow-hidden" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-200px 0px -200px 0px',
            amount: 0.2
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-729">
                {/* Consistent container styling for all visualization components */}
                <div className="w-full h-full bg-white rounded-[24px] shadow-[0_10px_40px_rgba(46,107,255,0.15)] border border-white/80 overflow-hidden" data-id="element-730">
                  {/* Content wrapper with consistent background gradient */}
                  <div className="w-full h-full bg-gradient-to-br from-[#f8faff] to-[#f0f5ff] relative" data-id="element-731">
                    {index === 0 ? <BrandedEmailInterface data-id="element-732" /> : index === 1 ? <AIVerificationDemo data-id="element-733" /> : <DocumentStreamVisualization data-id="element-734" />}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.section>;
    })}
      <div className="absolute inset-0 overflow-hidden" data-id="element-735">
        <motion.div className="absolute top-0 right-0 w-[40%] h-[40%] rounded-full bg-gradient-to-b from-[#2E6BFF]/10 to-transparent blur-[40px]" animate={{
        scale: [1, 1.1, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut'
      }} data-id="element-736" />
        <motion.div className="absolute bottom-0 left-0 w-[30%] h-[30%] rounded-full bg-gradient-to-t from-[#36D1DC]/10 to-transparent blur-[30px]" animate={{
        scale: [1, 1.2, 1],
        opacity: [0.2, 0.4, 0.2]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut',
        delay: 1
      }} data-id="element-737" />
      </div>
      <div className="relative z-10 flex justify-center py-16" data-id="element-738">
        <motion.a href="https://calendly.com/pigeondocuments" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC] hover:from-[#2059E2] hover:to-[#32C3CE] transition-all duration-300 shadow-[0_8px_16px_rgba(46,107,255,0.25)]" whileHover={{
        y: -2,
        boxShadow: '0 12px 20px rgba(46,107,255,0.3)'
      }} whileTap={{
        scale: 0.98
      }} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} data-id="element-739">
          Book a Call
          <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-740" />
        </motion.a>
      </div>
    </div>;
};